import type { ts } from '@funkey/shared';
import type { ReadonlyURLSearchParams } from 'next/navigation';

export class UrlPath implements Pick<Array<string>, 'length' | 'toString' | 'pop'> {
    private parts: string[];

    // eslint-disable-next-line no-useless-constructor
    constructor(path?: string) {
        this.parts = path && path !== '/' ? UrlPath._normalize(path).split('/') : [];
    }

    /** Removes leading slash to correctly split into parts */
    private static _normalize(path: string) {
        return path.startsWith('/') ? path.slice(1) : path;
    }

    static from(...segments: string[]) {
        const self = new UrlPath();
        self.parts = segments;
        return self;
    }

    /** Sets segment by index.
     * Allows for negative indices to go from last to first */
    setSegment(idx: number, segmentValue: string) {
        this.parts[idx < 0 ? this.parts.length + idx : idx] = segmentValue;

        return this;
    }

    setLastSegment(segmentValue: string) {
        return this.setSegment(this.parts.length - 1, segmentValue);
    }

    /** Sets the entire path to the given segments */
    set(segments: string[]) {
        this.parts = segments;
        return this;
    }

    toString() {
        return `/${this.parts.join('/')}`;
    }

    getLastSegment() {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.parts[this.parts.length - 1]!;
    }

    endsWith(segment: string) {
        return this.getLastSegment() === UrlPath._normalize(segment);
    }

    get segments() {
        return this.parts;
    }

    get length() {
        return this.parts.length;
    }

    pop() {
        return this.parts.pop();
    }

    up() {
        this.pop();
        return this;
    }

    push(...items: string[]) {
        this.parts.push(...items);
        return this;
    }

    unshift(...items: string[]) {
        this.parts.unshift(...items);
        return this;
    }

    copy() {
        return new UrlPath(this.toString());
    }
}

export declare namespace URLSearch {
    export type Shape = Partial<Record<string, string | string[]>>;
}
export class URLSearch<T extends URLSearch.Shape = {}> {
    private entries: [key: string, value: string][] = [];

    static fromString<T extends URLSearch.Shape = {}>(string: string): URLSearch<T> {
        return URLSearch.fromEntries(Array.from(new URLSearchParams(string).entries()));
    }

    static fromEntries<T extends URLSearch.Shape = {}>(
        entries: URLSearch['entries'],
    ): URLSearch<T> {
        const self = new URLSearch<T>();
        self.entries = entries;

        return self;
    }

    get<K extends keyof ts.Object.Keys.withType<T, string | undefined>>(
        name: K,
    ): (T[K] & string) | null {
        return (
            (this.entries.find(([entryName]) => entryName === (name as any))?.[1] as any) ?? null
        );
    }

    getAll<K extends keyof ts.Object.Keys.withType<T, string[]>>(
        name: K,
    ): (T[K] & string[]) | null {
        return this.entries
            .filter(([entryName]) => entryName === (name as any))
            .map(([_, value]) => value) as any;
    }

    set<K extends keyof T>(name: K, value: Exclude<T[K], undefined> | null) {
        this.entries = [
            ...Array.from(this.entries).filter(([searchName]) => searchName !== name),
            ...(Array.isArray(value)
                ? value.map(x => [name, x] as [string, string])
                : value === null
                  ? []
                  : [[name, value] as [string, string]]),
        ];

        return this;
    }

    copy(): URLSearch<T> {
        return URLSearch.fromEntries<T>(this.entries.map(([key, value]) => [key, value]));
    }

    static empty<T extends URLSearch.Shape = {}>(): URLSearch<T> {
        return URLSearch.fromEntries([]);
    }

    toString() {
        return this.entries.length > 0
            ? (`?${this.entries.map(([key, value]) => `${key}=${value}`).join('&')}` as const)
            : ('' as const);
    }
}

export class HREF<SearchParams extends URLSearch.Shape = {}> {
    constructor(
        public path: UrlPath,
        public search: URLSearch<SearchParams> = URLSearch.empty() as any,
    ) {}

    setPath(path: string | UrlPath) {
        this.path = typeof path === 'string' ? new UrlPath(path) : path;
        return this;
    }

    setSearchValue<T extends keyof SearchParams & string>(
        ...args: Parameters<typeof this.search.set<T>>
    ) {
        this.search.set(...args);
        return this;
    }

    toString(): string {
        return `${this.path.toString()}${this.search.toString()}`;
    }

    copy(): HREF<SearchParams> {
        return new HREF<SearchParams>(this.path.copy(), this.search.copy());
    }
}

export const searchParamStringOfKeys = (params: ReadonlyURLSearchParams, includeKeys: string[]) => {
    const array = [...params.keys()]
        .filter(key => includeKeys.includes(key))
        .map(key => `${key}=${params.get(key)}`);

    return array.length > 0 ? array.join('&') : null;
};
